import { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/browser";

type WizardErrorBoundaryProps = {
  wizardType: string;
  stepId?: string;
  fallback: (retry: () => void) => ReactNode;
  onError?: () => void;
  children: ReactNode;
};

type WizardErrorBoundaryState = {
  hasError: boolean;
};

// Wizards render above every page, so a throw inside one must not unmount the
// app underneath it. A class component because React only exposes error
// boundaries through getDerivedStateFromError / componentDidCatch.
export class WizardErrorBoundary extends Component<
  WizardErrorBoundaryProps,
  WizardErrorBoundaryState
> {
  state: WizardErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): WizardErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.captureException(error, {
      tags: {
        area: "wizard",
        wizardType: this.props.wizardType,
        wizardStep: this.props.stepId,
      },
      extra: { componentStack: info.componentStack },
    });
    this.props.onError?.();
  }

  retry = () => this.setState({ hasError: false });

  render() {
    if (this.state.hasError) {
      return this.props.fallback(this.retry);
    }
    return this.props.children;
  }
}
